import './locales/register';
import { createApp, watch } from 'vue';
import { createPinia } from 'pinia';
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { far } from '@fortawesome/free-regular-svg-icons'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { fr, en } from 'vuetify/locale'
import { getRouter, vuetifyOptions, registerConfigurationPanel, FondationArkivUploadPlugin, FondationHelpPlugin, useHelp, type IConfiguration, type IProfileTab } from '@fondationcss/ui';
import App from './app.vue';
import { getLanguage, useSdk, translate } from '@fondationcss/sdk';
import { getCustomRoutes } from './routes';
import { menus } from './sidebar-items';
import { installOfflineErrorSuppression } from '@/offline/offline-error-suppression';
import { installOfflineRedirect } from '@/offline/offline-redirect';
import { installFeatureRouteGuard } from '@/composables/feature-route-guard';
import { installSectionLandingGuard } from '@/composables/section-landing-guard';
import { installGricsImportAvailability } from '@/composables/grics-import-availability';
import { installFrequentationListGuard } from '@/composables/frequentation-list-guard';
import { installMeRefreshOnTenantInit } from '@/composables/refresh-me-on-tenant-init';
import { warmOfflineApiCache } from '@/offline/warm-offline-cache';
import { mergeVuetifyDefaults } from '@/utils/vuetify-defaults';
import { WikiPlugin } from '@fondationcss/wiki'
import VueApexCharts from 'vue3-apexcharts';
import PageHeader from '@/components/page-header.vue';
import ProfileActions from '@/components/profile-actions.vue';
import GricsApiPanel from '@/components/GricsApiPanel.vue';

import 'vuetify/styles';
import '@fontsource/public-sans/400.css';
import '@fontsource/public-sans/500.css';
import '@fontsource/public-sans/600.css';
import '@fontsource/public-sans/700.css';
import '@fondationcss/ui/dist/style.css';
import '@fondationcss/wiki/dist/style.css';
import '@fondationcss/markdown/dist/style.css';
import '@fondationcss/markdown/dist/markdown.css';
import '@fondationcss/grics/dist/index.css';
import "@/styles.css";
import "@/styles/tables.css";
import 'highlight.js/styles/github.css';

// The GRICS gateway diagnostics belong next to the settings they exercise: the configuration page
// already edits Grics:Authority and friends, but nothing there proves the values actually work.
// Registered rather than imported by the page, because @fondationcss/ui must not depend on an
// opt-in satellite (mirrors Perspective's own registration).
registerConfigurationPanel({
  value: 'grics-api',
  get title() { return translate('parcours.app.grics-api'); },
  icon: 'fas fa-plug',
  parent: 'grics',
  component: GricsApiPanel,
});

const config = {
  AppName: 'Parcours',
  SiteNameShort: 'Parcours',
  SiteNameLong: 'Byrnu Parcours',
  LogoLine1: 'Byrnu',
  LogoLine2: 'Parcours',
  Copyright: 'Copyright © 2026',
  SidebarItems: [],
  MenuGroupsOrientation: 'vertical',
  Menus: menus,
  EnableAccessRequestButton: false,

  DefaultLayout: 'byrnu-red-app',
  ThemeColor: '#3460a8',
  // Calendars delegate PDF preparation to the product's reports area.
  CalendarReportRoute: '/gestion/rapports?type=calendar',
  Logo: '/images/parcours.svg',  // Generic logo fallback (URL)
  SiteLogo: '/images/parcours.svg',  // Logo for login/init screens (URL)
  // SiteLogoSvg: '<svg>...</svg>'  // Logo for login/init screens (raw SVG - takes priority over SiteLogo and Logo)
  SiteLogoAlt: '/images/parcours-alt.svg',  // Logo for header (URL)
  // SiteLogoAltSvg: '<svg>...</svg>'  // Logo for header (raw SVG - takes priority over all others)
  HideLoginLogo: true,
  // Inline help (Osmos): the configuration (instance + public token) is loaded
  // at runtime from the server settings (`/api/core/settings` → `help`), then
  // applied through useHelp().configure() below. A single build therefore serves
  // every environment with its own Osmos instance.
} as IConfiguration;

/**
 * The dark theme, registered here because the Fondation does not do it.
 *
 * `default-layout.vue` sets `theme.global.name.value = 'dark'` when the toggle is on, but the
 * Fondation's options only declare `light` and `byrnuRed`, both `dark: false`. Vuetify therefore
 * fell back on ITS own default dark theme — verified in the installed package —, some of whose
 * values are frankly unusable:
 *
 *  - `on-surface-variant: '#000000'`. That variant is meant to go ON `surface-variant`, which is
 *    light in Vuetify; on a dark surface it is black on black. And that is exactly what the
 *    Fondation uses for `SummaryMetrics`' label and hint, and what twelve Parcours screens used
 *    for their secondary text. It is the bulk of the "black text for no reason" seen in review.
 *  - `surface-variant: '#c8c8c8'`, a light grey that has no business on a dark background.
 *  - `surface-bright: '#ccbfd6'`, a lilac. `VSwitch` is its only consumer — the thumb of every
 *    switch — so every switch of the app wore a lilac thumb in dark mode (« Mode Express », #1098).
 *
 * Vuetify MERGES a declared theme on top of its own (`parseThemeOptions`:
 * `mergeDeep(defaults.themes.dark, theme)` as soon as the key is called `dark`), so this block
 * only needs to fix what is wrong — the rest of the dark palette stays Vuetify's, that is, what
 * is already rendered today. No change of appearance other than the texts becoming readable
 * again.
 *
 * Fixing it here rather than through a CSS override targeting `.summary-metrics__label`: the
 * token repairs every consumer at once, including the Fondation's, without depending on the class
 * names of a published package. Eventually the Fondation should declare its own dark theme with
 * the Byrnu palette; this block will then disappear.
 */
const darkTheme = {
  dark: true,
  colors: {
    'surface-variant': '#3b4653',
    'on-surface-variant': '#c9d4e0',
    // The light blue-grey of `on-surface-variant`'s family, a notch brighter: a thumb must read
    // over both the dark off track and the primary on track.
    'surface-bright': '#e3e9f0',
  },
} as const;

const vuetify = createVuetify({
  components,
  directives,
  ...vuetifyOptions,
  // Selected states are the theme primary, not the near-black Vuetify falls back to; the
  // Fondation's own defaults are merged, never replaced (see the module).
  defaults: mergeVuetifyDefaults(vuetifyOptions.defaults as Record<string, unknown> | undefined),
  theme: {
    ...vuetifyOptions.theme,
    themes: {
      ...(vuetifyOptions.theme as { themes?: Record<string, unknown> } | undefined)?.themes,
      dark: darkTheme,
    },
  },
  locale: {
    // Vuetify's own texts (pagination, data tables…) follow the application language.
    locale: getLanguage() === 'en-CA' ? 'en' : 'fr',
    messages: { fr, en }
  }
})
watch(getLanguage, (language) => {
  vuetify.locale.current.value = language === 'en-CA' ? 'en' : 'fr';
});
const app = createApp(App);
const router = getRouter(getCustomRoutes as () => []);
// Offline support (#377): when offline, keep the user on Présences (the only offline-capable
// page). Registered before the first navigation so an offline cold-start lands there too.
installOfflineRedirect(router);
app.provide('config', config)
app.provide('sdkOptions', { apiUrl: import.meta.env.VITE_API_URL ?? undefined })
app.provide('profileTabs', [
  {
    value: 'parcours-actions',
    icon: 'fas fa-bolt',
    get label() { return translate('parcours.app.profileActions'); },
    component: ProfileActions,
    insertLocation: '222', // insert before « Paramètres »
  },
] satisfies IProfileTab[])
app.use(vuetify);
app.component('font-awesome-icon', FontAwesomeIcon)
app.component('PageHeader', PageHeader)
library.add(fas)
library.add(far)
app.use(createPinia());
app.use(PerfectScrollbarPlugin);
app.use(router);

app.use(VueApexCharts);
app.use(FondationHelpPlugin)
// Arkiv upload widget (#634): registers <ArkivUploadButton>. The
// <GlobalArkivUpload> bootstrap is already rendered by the fondation App root and
// configures itself at runtime from the server settings (`ArkivUpload:ApiBase` →
// settings.arkivUpload, loaded by /api/core/settings, laid down in P0). Like the
// Osmos help, the system stays inert as long as the server provides no
// configuration — a single build serves every environment.
app.use(FondationArkivUploadPlugin)

// Configure inline help (Osmos) at runtime from the server settings, loaded by
// the SDK into `settings.help` (see SettingsExtension.cs). Reactive because the
// settings arrive asynchronously during SDK init; stays inert until a token exists.
const sdk = useSdk();

// Feature-gated routes (#812): a route whose `meta.feature` flag is off redirects to its
// section's dashboard. Registered before mount so the initial navigation is judged too.
installFeatureRouteGuard(router, sdk.Features);

// A section opens on its first entry whatever the user's rights (#1167): send a user the menu
// hides that entry from to the first one they can see.
installSectionLandingGuard(router, { me: sdk.me, flags: sdk.Features.flags });
installGricsImportAvailability(router, sdk.me);
installFrequentationListGuard(router, sdk.me);

// `initializeApp()` can hand back a profile resolved before the caller received the Administrateur
// role: reload it once the tenant is initialized so the setup assistant sees the permission (#1187).
installMeRefreshOnTenantInit({
  me: sdk.me,
  // Silent like the SDK's own reads of /core/me: a failure is swallowed by the composable.
  fetchMe: () => sdk.fetchWrapper.get<NonNullable<typeof sdk.me.value>>('/core/me', { errorHandler: null }),
});

// `help` is delivered by the server settings (passthrough) and typed in newer
// @fondationcss/sdk; cast keeps it compiling against the currently published type.
type HelpSettings = { publicToken: string; apiBase?: string; scopeName?: string };
// Held back until the tenant is initialized: pre-init the help token isn't valid
// and RBAC isn't wired up, so loading the widget would just fail. The watch
// re-runs when `isTenantInitialized` flips true.
watch(
  [
    () => (sdk.settings.value as { help?: HelpSettings } | null)?.help,
    sdk.isTenantInitialized,
  ] as const,
  ([help, ready]) => {
    if (ready && help?.publicToken) {
      useHelp().configure({
        publicToken: help.publicToken,
        apiBase: help.apiBase,
        scopeName: help.scopeName ?? 'Parcours',
      });
    }
  },
  { immediate: true },
);
// Type assertion needed due to Vue 3.5+ stricter Plugin generic constraints
app.use(WikiPlugin as Parameters<typeof app.use>[0], { router })
app.mount('#app');

// Offline support (#377): keep network-error snackbars from spamming during an offline session,
// scoped to the offline-capable Présences page so other pages still report network errors.
// Installed after mount so it wraps the handler the app root sets on mount.
installOfflineErrorSuppression(sdk, () => router.currentRoute.value.name === 'enseignement-presences');

// Offline boot (#377): warm the boot-critical API GETs into the SW cache on this online load so a
// single visit is enough for the app to boot offline later (the Fondation boot gate fetches
// /core/settings and reloads on failure). Best-effort; no-op offline.
void warmOfflineApiCache(import.meta.env.VITE_API_URL ?? undefined);

// Disables the browser's autofill suggestions on every field: the Arkiv forms
// contain no personal data and Chrome's suggestions are always out of context
// there. Vuetify's `defaults` cannot do it (it only applies declared props, and
// `autocomplete` is an attribute passed to the input), hence applying it at the
// DOM level.
// A field can opt out by declaring an autocomplete attribute itself.
new MutationObserver(() => {
  document
    .querySelectorAll('input:not([autocomplete]), textarea:not([autocomplete])')
    .forEach((el) => el.setAttribute('autocomplete', 'off'));
}).observe(document.body, { childList: true, subtree: true });
